import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Save, History, Check, Link2 } from 'lucide-react';

// Small popover showing the change history for one (batch, student)
function HistoryPopover({ batchId, studentId }) {
  const [entries, setEntries] = useState(null);
  const load = () => {
    if (entries !== null) return;
    api.get(`/training/reports/batch-remarks/${batchId}/student/${studentId}/history`)
      .then(r => setEntries(r.data.entries || []))
      .catch(() => setEntries([]));
  };
  return (
    <Popover onOpenChange={(open) => { if (open) load(); }}>
      <PopoverTrigger asChild>
        <button className="text-slate-400 hover:text-violet-600 inline-flex items-center gap-1 text-[10px]"
          data-testid={`pdp-mock-history-${batchId}-${studentId}`} title="View change history">
          <History className="w-3 h-3" />Log
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-3 py-2 border-b bg-slate-50 text-[10px] uppercase font-semibold text-slate-500">Change history · {entries?.length ?? '…'}</div>
        <div className="max-h-64 overflow-y-auto text-[11px] divide-y divide-slate-100">
          {entries === null ? (
            <p className="px-3 py-4 text-slate-400 text-center">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="px-3 py-4 text-slate-400 text-center italic">No changes yet.</p>
          ) : entries.map((e, i) => (
            <div key={i} className="px-3 py-2">
              <p className="text-[9px] uppercase text-slate-400 font-semibold">
                {e.field} · {e.changed_by || '—'} <span className="text-slate-300">({e.changed_by_role || 'user'})</span>
                <span className="float-right text-slate-400 font-normal normal-case">{new Date(e.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </p>
              <p className="text-slate-500 mt-0.5">
                <span className="line-through decoration-rose-300 text-slate-400">{e.old_value || '(empty)'}</span>
                <span className="mx-1 text-slate-400">→</span>
                <span className="text-emerald-700 font-medium">{e.new_value || '(empty)'}</span>
              </p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Reusable inline editor. Renders 3 inputs + Save button.
// Props:
//   batchId, studentId  — target row
//   initial: { pdp, mock_interview, mock_interview_link }
//   endpoint: 'admin' | 'trainer' — controls which PUT path to hit
//   onSaved?(nextValues) callback so parent can refresh cached rows
//   compact?: boolean — narrower inputs for table use
export function PdpMockEditor({ batchId, studentId, initial = {}, endpoint = 'admin', onSaved, compact = false }) {
  const [pdp, setPdp] = useState(initial.pdp || '');
  const [mock, setMock] = useState(initial.mock_interview || '');
  const [link, setLink] = useState(initial.mock_interview_link || '');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    setPdp(initial.pdp || '');
    setMock(initial.mock_interview || '');
    setLink(initial.mock_interview_link || '');
  }, [batchId, studentId, initial.pdp, initial.mock_interview, initial.mock_interview_link]);

  const dirty = pdp !== (initial.pdp || '') || mock !== (initial.mock_interview || '') || link !== (initial.mock_interview_link || '');

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const payload = { pdp, mock_interview: mock, mock_interview_link: link };
      const url = endpoint === 'trainer'
        ? `/trainer/batches/${batchId}/students/${studentId}/remarks`
        : `/training/reports/batch-remarks/${batchId}/student/${studentId}`;
      await api.put(url, payload);
      toast.success('Saved');
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
      if (onSaved) onSaved(payload);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const cellCls = compact ? 'text-[11px] h-7' : 'text-xs h-8';

  return (
    <div className={`grid ${compact ? 'grid-cols-[1fr_130px_1.5fr_auto]' : 'grid-cols-1 md:grid-cols-[1fr_150px_2fr_auto]'} gap-2 items-center`}
      data-testid={`pdp-mock-editor-${batchId}-${studentId}`}>
      <Input value={pdp} onChange={(e) => setPdp(e.target.value)} placeholder="PDP status…"
        className={cellCls} data-testid={`pdp-input-${batchId}-${studentId}`} />
      <Select value={mock || 'none'} onValueChange={(v) => setMock(v === 'none' ? '' : v)}>
        <SelectTrigger className={cellCls} data-testid={`mock-select-${batchId}-${studentId}`}><SelectValue placeholder="Mock" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Not Set</SelectItem>
          <SelectItem value="Completed">Completed</SelectItem>
          <SelectItem value="Scheduled">Scheduled</SelectItem>
          <SelectItem value="Not Done">Not Done</SelectItem>
        </SelectContent>
      </Select>
      <div className="relative">
        <Link2 className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
        <Input type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://... video / transcript"
          className={`${cellCls} pl-6`} data-testid={`link-input-${batchId}-${studentId}`} />
      </div>
      <div className="flex items-center gap-1.5">
        <Button size="sm" onClick={save} disabled={!dirty || saving}
          className={`h-7 text-[11px] ${justSaved ? 'bg-emerald-600' : 'bg-violet-600 hover:bg-violet-700'}`}
          data-testid={`pdp-mock-save-${batchId}-${studentId}`}>
          {justSaved ? <><Check className="w-3 h-3 mr-0.5" />Saved</> : <><Save className="w-3 h-3 mr-0.5" />{saving ? '…' : 'Save'}</>}
        </Button>
        <HistoryPopover batchId={batchId} studentId={studentId} />
      </div>
    </div>
  );
}

export default PdpMockEditor;
